<template>
  <component
    :is="parentElement"
    v-if="!isDismissed"
    :to="computedLink"
    role="note"
    :class="[
      'info-text-component flex flex-wrap items-stretch justify-between gap-x-3 rounded-lg border text-sm no-underline',
      TYPE_CLASSES[type] || TYPE_CLASSES.info,
      '[&_a]:font-semibold [&_a]:underline [&_a]:underline-offset-2',
      '[&_.budget]:whitespace-nowrap',
    ]"
  >
    <div :class="[ 'flex min-w-0 flex-1 items-start gap-2.5', large ? 'p-4' : 'px-3 py-2.5' ]">
      <loader-circle-icon v-if="icon === 'loader'" :class="[ 'mt-0.5 size-4 shrink-0 animate-spin', ICON_CLASSES[type] || ICON_CLASSES.info ]" />
      <i v-else-if="icon" :class="[ 'icon mt-0.5 size-4 shrink-0', icon, ICON_BG_CLASSES[type] || ICON_BG_CLASSES.info ]" />
      <component :is="typeIcon" v-else :class="[ 'mt-0.5 size-4 shrink-0', ICON_CLASSES[type] || ICON_CLASSES.info ]" />

      <div class="min-w-0 flex-1 text-foreground [&_small]:mt-1 [&_small]:block [&_small]:text-xs [&_small]:text-muted-foreground">
        <div v-if="$slots.title" class="font-semibold">
          <slot name="title" />
        </div>

        <transition-expand v-if="collapsible">
          <div v-if="isExpanded" class="mt-2">
            <slot />
          </div>
        </transition-expand>
        <slot v-else />
      </div>
    </div>

    <div v-if="hasActions" class="flex items-center gap-1 pr-2 max-sm:w-full max-sm:justify-end max-sm:pb-2">
      <span v-if="computedLink" class="px-2 text-xs font-medium">
        {{ buttonLabel ?? tr('View') }}
      </span>
      <ui-button
        v-if="collapsible"
        variant="ghost"
        size="sm"
        class="text-xs"
        @click.stop.prevent="() => isExpanded = !isExpanded"
      >
        {{ isExpanded ? tr('Show less') : tr('Details') }}
      </ui-button>
      <ui-button
        v-if="closable"
        variant="ghost"
        size="icon-sm"
        :aria-label="tr('Close')"
        @click.stop.prevent="() => dismiss()"
      >
        <x-icon />
      </ui-button>
    </div>
  </component>
</template>

<script>
import { Info, LoaderCircle, TriangleAlert, X } from '@lucide/vue';

import TransitionExpand from '@/ui/components/TransitionExpand.vue';

const STORAGE_PREFIX = 'info-text.dismissed.';

// Tinted surface per message type.
const TYPE_CLASSES = {
  info: 'border-primary/25 bg-primary/8',
  warning: 'border-warning/40 bg-warning/10',
  error: 'border-destructive/35 bg-destructive/8',
};
const ICON_CLASSES = {
  info: 'text-primary',
  warning: 'text-warning',
  error: 'text-destructive',
};
// Mask icons (the `icon` prop, src/assets/icons.css) take their color from the background.
const ICON_BG_CLASSES = {
  info: 'bg-primary',
  warning: 'bg-warning',
  error: 'bg-destructive',
};

export default {
  name: 'InfoText',
  components: {
    LoaderCircleIcon: LoaderCircle,
    TransitionExpand,
    XIcon: X,
  },
  props: {
    type: {
      type: String,
      required: false,
      default: () => 'info',
    },
    icon: {
      type: String,
      required: false,
      default: () => null,
    },
    link: {
      type: String,
      required: false,
      default: () => null,
    },
    buttonLabel: {
      type: String,
      required: false,
      default: () => null,
    },
    large: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    closable: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    collapsible: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    dismissKey: {
      type: String,
      required: false,
      default: () => null,
    },
    dismissTtlHours: {
      type: Number,
      required: false,
      default: () => null,
    },
  },
  emits: [ 'close' ],
  data() {
    return {
      TYPE_CLASSES,
      ICON_CLASSES,
      ICON_BG_CLASSES,
      isExpanded: false,
      isDismissed: this.isPersistentlyDismissed(),
    };
  },
  computed: {
    computedLink() {
      if (!this.$root.isInOriginalCompany) {
        return null;
      }

      return this.link;
    },
    parentElement() {
      if (this.computedLink) {
        return 'router-link';
      }

      return 'div';
    },
    hasActions() {
      return this.computedLink || this.closable || this.collapsible;
    },
    typeIcon() {
      return this.type === 'warning' || this.type === 'error' ? TriangleAlert : Info;
    },
  },
  methods: {
    storageKey() {
      return STORAGE_PREFIX + this.dismissKey;
    },
    isPersistentlyDismissed() {
      if (!this.dismissKey) {
        return false;
      }

      try {
        const dismissedAt = Number(window.localStorage.getItem(this.storageKey()));
        if (!dismissedAt) {
          return false;
        }

        if (this.dismissTtlHours === null) {
          return true;
        }

        return (Date.now() - dismissedAt) < (this.dismissTtlHours * 60 * 60 * 1000);
      }
      catch (e) {
        return false;
      }
    },
    dismiss() {
      this.$emit('close');

      if (!this.dismissKey) {
        return;
      }

      this.isDismissed = true;
      try {
        window.localStorage.setItem(this.storageKey(), String(Date.now()));
      }
      catch (e) {
        // eg. private browsing - not critical, the panel will hide for the session
      }
    },
  },
};
</script>
